import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, type IsActiveMatchOptions } from '@angular/router';
import { HlmNavtab } from '@lite-workspace/shared/helm-ui/navtab';
import { HlmTopbar, HlmTopbarActions } from '@lite-workspace/shared/helm-ui/topbar';
import { OnboardingAnchorDirective } from '@lite-workspace/shared/onboarding-service';
import { HlmAppShellCount } from './hlm-app-shell-count';

/** One centred top-bar tab. `link` goes straight to `routerLink`, so both the string and the commands form work. */
export interface NavItem {
  label: string;
  link?: string | unknown[];
  /** Set instead of `link`: the tab is a button that runs this (opens a window) and never reads as active. */
  action?: () => void;
  queryParams?: Record<string, string>;
  /**
   * Prefix match by default. A tab whose route is the PARENT of its siblings (a section index next to
   * its own sub-sections) sets this, or it stays lit on every sibling and two tabs read as active.
   */
  exact?: boolean;
  /** Counter after the label; omitted = label only. */
  count?: number;
  /** `accent` turns the count into an accent pill (unread); `muted` (default) keeps the plain label. */
  countTone?: 'muted' | 'accent';
  /** `plain` (default): the count as `countTone` paints it. `comments`: the accent comment circle on the label's
   *  corner, hidden at 0 — `countTone` does not apply. */
  countStyle?: 'plain' | 'comments';
  /** Spoken form of `count` in the tab's accessible name («3 нерешённых»); omitted = the bare number. */
  countLabel?: string;
  /** Onboarding record whose coach card stands on this tab; the host sets it only while the card may show. */
  coachAnchor?: string | null;
}

/**
 * `exact` pins the PATH only. Angular's `{ exact: true }` shorthand also demands an exact query-param
 * match, so a page's own params (`/galleries/<id>?created=1`) would unlight its tab.
 */
export const NAVTAB_EXACT_MATCH: IsActiveMatchOptions = {
  paths: 'exact',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

/** Prefix match — a tab stays lit on its own sub-sections. Angular's `{ exact: false }`, spelled out. */
export const NAVTAB_PREFIX_MATCH: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'subset',
  fragment: 'ignored',
  matrixParams: 'subset',
};

/** Below this many pixels of scroll the bar keeps its flat state — one threshold for every product shell. */
const SCROLL_SHADOW_THRESHOLD = 4;

/** The header-height pair as one class string: an app root that must publish the same value OUTSIDE
 *  the shell (a toast host sitting next to the router outlet) reuses it instead of re-typing it. */
export const HLM_APP_SHELL_HEADER_H_CLASS =
  '[--app-shell-header-h:calc(var(--spacing)*10.5)] md:[--app-shell-header-h:calc(var(--spacing)*11)]';

/**
 * The one cabinet shell for every LUMIR product: scroll container, sticky topbar, centred tabs,
 * and the projection slots products fill with their own logo / actions / banners.
 *
 * Header height is published as `--app-shell-header-h` so sticky product chrome (organizer banner,
 * replacement headers) can offset itself without re-typing the breakpoint pair.
 */
@Component({
  selector: 'hlm-app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: `flex min-h-0 flex-1 flex-col ${HLM_APP_SHELL_HEADER_H_CLASS}`,
  },
  imports: [
    CdkScrollable,
    RouterLink,
    RouterLinkActive,
    HlmNavtab,
    HlmTopbar,
    HlmTopbarActions,
    OnboardingAnchorDirective,
    HlmAppShellCount,
  ],
  template: `
    <!-- cdkScrollable: the page scrolls HERE, not on the document. Without the registration CDK's
         reposition strategy never hears this scroll and every attached overlay stays where it opened. -->
    <div cdkScrollable class="shell-scroll flex min-h-0 flex-1 flex-col overflow-y-auto" (scroll)="onScroll($event)">
      <ng-content select="[hlmAppShellHeader]" />

      @if (!hideHeader()) {
        <header
          hlmTopbar="cabinet"
          class="sticky top-0 z-20 flex h-[var(--app-shell-header-h)] shrink-0 items-center justify-between px-3.5 print:hidden md:px-5 lg:grid lg:grid-cols-[var(--layout-portal-header)]"
          [class.scrolled]="scrolled()"
        >
          <!-- lg:min-w-auto: an fr track gets only what the tabs leave, so min-w-0 would squeeze the logo under them -->
          <div class="flex h-full min-w-0 items-center lg:min-w-auto">
            <ng-content select="[hlmAppShellLogo]" />
          </div>

          <nav class="no-scrollbar hidden h-full min-w-0 items-center gap-0.5 overflow-x-auto lg:flex">
            <!-- track by SLOT, not by label: «Другое» and «Списки» share one slot, so the expanding row
                 relabels that tab in place and only the extra tab animates. Tracking the label instead
                 destroyed «Другое» and created two tabs at once — the row overshot by the leaving tab's
                 width and folded back, the two-step jerk. -->
            @for (item of tabs(); track $index) {
              @if (item.action; as action) {
                <!-- Opens a window, not a route: no routerLinkActive, so it never reads as the current tab. -->
                <button
                  type="button"
                  hlmNavtab
                  class="cursor-pointer"
                  animate.enter="navtab-swap"
                  animate.leave="navtab-swap navtab-swap-out"
                  (click)="action(); tabClicked.emit(item.label)"
                >
                  <span>{{ item.label }}</span>
                </button>
              } @else {
                <!-- draggable=false: a few px of hand travel mid-press start a native link drag, which eats the click -->
                <a
                  draggable="false"
                  [routerLink]="item.link"
                  [queryParams]="item.queryParams ?? null"
                  routerLinkActive="tab-on"
                  [routerLinkActiveOptions]="item.exact ? exactMatch : prefixMatch"
                  ariaCurrentWhenActive="page"
                  hlmNavtab
                  animate.enter="navtab-swap"
                  animate.leave="navtab-swap navtab-swap-out"
                  [onboardingAnchor]="item.coachAnchor ?? null"
                  (click)="tabClicked.emit(item.label)"
                >
                  <!-- label in its own span: Angular drops whitespace-only nodes BETWEEN elements, so
                       the count never inherits a stray space from however the formatter breaks this line -->
                  <span>{{ item.label }}</span>
                  <hlm-app-shell-count [item]="item" />
                </a>
              }
            }
          </nav>

          <div hlmTopbarActions class="h-full shrink-0 justify-self-end">
            <ng-content select="[hlmAppShellActions]" />
          </div>
        </header>
      }

      <ng-content select="[hlmAppShellBanner]" />

      <main class="flex min-h-0 flex-1 flex-col">
        <ng-content />
      </main>
    </div>
  `,
  styles: `
    /* No trackpad rubber-band at the scroll edges (the sticky bar would ride it) and no sideways sway. */
    .shell-scroll {
      overflow-x: hidden;
      overscroll-behavior: none;
    }
    /* The bar sits above this box: a scrollbar that comes and goes on navigation would
       re-centre the tabs on every page swap. The gutter is always reserved, so the tabs
       never move; the thumb is the slim one the cabinet uses everywhere.
       Never pair with scrollbar-width — Chrome drops every ::-webkit-scrollbar rule below it.
       Touch screens (Tailwind's pointer-coarse) keep the native overlay scrollbar: a styled
       one reserves its 10px out of the page column. */
    @media not (pointer: coarse) {
      .shell-scroll {
        scrollbar-gutter: stable;
      }
      .shell-scroll::-webkit-scrollbar {
        width: calc(var(--spacing) * 2.5);
        height: calc(var(--spacing) * 2.5);
      }
      .shell-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      /* The transparent border + padding-box clip inset the 10px track down to a 4px thumb. */
      .shell-scroll::-webkit-scrollbar-thumb {
        background: var(--line2);
        border: calc(var(--spacing) * 0.75) solid transparent;
        border-radius: var(--r-pill);
        background-clip: padding-box;
      }
      .shell-scroll::-webkit-scrollbar-thumb:hover {
        background: var(--line3);
      }
    }

    /* The tab row scrolls sideways, so it clips whatever sticks out of it: the count circle overhangs
       the last tab by up to a «99+» width. Equal room on both ends keeps the row centred. */
    nav {
      padding-inline: calc(var(--spacing) * 3);
    }

    /* A tab row that grows or shrinks («Другое» → «Списки»/«Статистика») slides open and shut
       on one clock instead of popping: width and opacity travel together, both directions. */
    @keyframes hlm-navtab-swap {
      from {
        max-width: 0;
        opacity: 0;
      }
      /* Cap sits just above a real tab's width: a far larger one finishes the visible growth
         halfway through the clock and leaves the rest as a pure fade — a second step. */
      to {
        max-width: calc(var(--spacing) * 32);
        opacity: 1;
      }
    }
    .navtab-swap {
      overflow: hidden;
      animation: hlm-navtab-swap var(--t-mid) var(--ease-smooth);
    }
    .navtab-swap-out {
      animation-direction: reverse;
    }
    @media (prefers-reduced-motion: reduce) {
      .navtab-swap {
        animation: none;
      }
    }
  `,
})
export class HlmAppShell {
  /** Centred navigation; empty means the product ships no tabs on this surface. */
  readonly tabs = input<NavItem[]>([]);
  /** Products that swap in their own header for a screen hide the standard one instead of forking the shell. */
  readonly hideHeader = input(false);
  readonly tabClicked = output<string>();

  protected readonly exactMatch = NAVTAB_EXACT_MATCH;
  protected readonly prefixMatch = NAVTAB_PREFIX_MATCH;

  private readonly _scrolled = signal(false);
  /** Public so projected replacement headers can share the one shadow state: `[class.scrolled]="shell.scrolled()"`. */
  readonly scrolled = this._scrolled.asReadonly();

  protected onScroll(event: Event): void {
    this._scrolled.set((event.target as HTMLElement).scrollTop > SCROLL_SHADOW_THRESHOLD);
  }
}
